import { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtUSD } from "@/lib/api";

const MODES = [["both", "Base + Downside"], ["base", "Base"], ["down", "Downside"]];

export function ProjectionChart({ projection: p }) {
  const [mode, setMode] = useState("both");
  const [metric, setMetric] = useState("dscr");
  if (!p) return <div className="panel p-6 text-sm text-slate-500">Projection needs rent, occupancy, expenses and loan terms.</div>;
  const data = p.rows.map((r) => ({ year: `Y${r.year}`, base: metric === "dscr" ? r.base_dscr : r.base_noi, down: metric === "dscr" ? r.down_dscr : r.down_noi }));
  const fmt = (v) => (metric === "dscr" ? `${v.toFixed(2)}x` : fmtUSD(v, true));
  return (
    <div className="panel" data-testid="projection-chart">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap gap-2 justify-between items-center">
        <div><span className="eyebrow">7-year projection</span><span className="ml-2 text-[10px] text-slate-500">Bank standard scenarios</span></div>
        <div className="flex gap-1">
          {[["dscr", "DSCR"], ["noi", "NOI"]].map(([k, l]) => (
            <button key={k} data-testid={`projection-metric-${k}`} onClick={() => setMetric(k)} className={`text-[11px] px-2 py-1 rounded transition-colors ${metric === k ? "bg-slate-200 text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
          <span className="w-px bg-slate-100 mx-1" />
          {MODES.map(([k, l]) => (
            <button key={k} data-testid={`projection-mode-${k}`} onClick={() => setMode(k)} className={`text-[11px] px-2 py-1 rounded transition-colors ${mode === k ? "bg-sky-600/20 text-sky-800" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="h-60 px-2 pt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: 8, right: 16 }}>
            <CartesianGrid stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="year" stroke="#475569" fontSize={11} tickLine={false} />
            <YAxis stroke="#475569" fontSize={11} tickLine={false} tickFormatter={fmt} width={64} domain={["auto", "auto"]} />
            <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #cbd5e1", fontSize: 12 }} formatter={(v) => fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {metric === "dscr" && <ReferenceLine y={1.25} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: "1.25x covenant", fill: "#f59e0b", fontSize: 10, position: "insideTopRight" }} />}
            {mode !== "down" && <Line dataKey="base" name="Base case" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 2 }} />}
            {mode !== "base" && <Line dataKey="down" name="Downside" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 2 }} />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid md:grid-cols-2 gap-4 p-4 border-t border-slate-200 text-xs">
        {[["base", "Base assumptions", "text-sky-700"], ["downside", "Downside assumptions", "text-red-700"]].map(([k, l, c]) => (
          <div key={k} data-testid={`assumptions-${k}`}>
            <div className={`font-medium mb-1 ${c}`}>{l}</div>
            <ul className="space-y-0.5 text-slate-600">{p.assumptions[k].map((a) => <li key={a}>· {a}</li>)}</ul>
          </div>
        ))}
      </div>
      <div className="px-4 pb-4 text-xs num text-slate-600" data-testid="downside-summary">
        Debt service {fmtUSD(p.annual_debt_service)} /yr · Min downside DSCR <span className={p.min_down_dscr < 1.25 ? "text-red-600" : "text-emerald-600"}>{p.min_down_dscr.toFixed(2)}x</span>
        {p.downside_breach_years.length > 0 && <> · Covenant breach in years {p.downside_breach_years.join(", ")}</>}
      </div>
    </div>
  );
}
